import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { showToast } from '../../../utils/toast';
import { companyURL, currencyURL, getRolesURL, modulesURL, userDelURL, userModulesURL, userURL } from '../api/url';
import { signupURL } from '../../Auth/api/url';
import { authStore } from '../../Auth';
import { SYSTEM_ADMIN, TRANSLATOR } from '../../../constants/constants';
import { INITIAL_USER } from '../constants/user.constants';
import { companyStore } from '../../Company';
import { handleFetch } from '../../../utils/handleFetch';

class UserStore {
	users = [];
	totalUsers = 0;
	roles = [];
	companies = [];
	errors = {};
	currencies = [];
	selectedCurrency = {
		cnp: 'USD',
		onboard: 'USD',
	};
	initialUser = INITIAL_USER;
	accessToCustom = null;
	isUserModalOpen = false;
	selectedCompanyId = null;
	page = 1;
	limit = 10;
	search = '';
	selectedUserId = null;
	selectedUserDisplayName = '';
	isDeleteConfirmModalOpen = false;
	customPermission = null;

	constructor() {
		makeAutoObservable(this);
	}

	setPage = (v) => (this.page = v);

	setSearch = (v) => (this.search = v);

	openUserModal = (companyId) => {
		this.selectedCompanyId = companyId;
		if (this.selectedCompanyId) this.initialUser.companyId = this.selectedCompanyId;
		this.isUserModalOpen = true;
	};

	hideUserModal = () => (this.isUserModalOpen = false);

	closeUserModal = () => {
		this.isUserModalOpen = false;
		this.initialUser = INITIAL_USER;
		this.errors = {};
	};

	_handleChange = (e) => {
		const { id, value } = e.target;
		this.initialUser[id] = value;
	};

	handleChangeModuleAccess = (key, access, value) => {
		this.accessToCustom.filter((m) => m.moduleName.key === key)[0][access] = value;
	};

	getUsers = async () => {
		return await handleFetch({
			url: userURL() + `?page=${this.page}&limit=${this.limit}&search=${this.search}`,
			method: 'GET',
			onSuccess: (data) =>
				runInAction(() => {
					const { total, users } = data;
					this.users = users;
					this.totalUsers = total;
				}),
		});
	};

	updateCurrency = async (module, currency) => {
		return await handleFetch({
			url: currencyURL(module, currency),
			method: 'PUT',
			onSuccess: (data) => runInAction(() => (this.selectedCurrency[module] = data)),
		});
	};

	getCurrency = async (module) => {
		const companyId = companyStore.selectedCompanyId;
		return await handleFetch({
			url: currencyURL(module, companyId ? companyId : authStore.user?.companyId),
			method: 'GET',
			onSuccess: (data) => runInAction(() => (this.selectedCurrency[module] = data)),
		});
	};

	getModulesByUser = async (id) => {
		return await handleFetch({
			url: userModulesURL(id),
			method: 'GET',
			onSuccess: (data) => runInAction(() => (this.accessToCustom = data.length > 0 ? data : this.customPermission)),
		});
	};

	getModules = async () => {
		const role = authStore.role;
		return await handleFetch({
			url: modulesURL,
			method: 'GET',
			onSuccess: (data) =>
				runInAction(() => {
					this.customPermission = data.flatMap((item) => {
						if (item.moduleName.moduleType === 'parent') {
							const parentObj = {
								moduleName: {
									key: item.moduleName.key,
									value: item.moduleName.value,
									access: true,
									moduleType: 'parent',
								},
								create: true,
								view: true,
								update: true,
								drop: true,
							};

							const children = (item.subModule ?? []).map((subItem) => ({
								moduleName: {
									key: subItem.key,
									value: subItem.value,
									moduleType: item.moduleName.key + '__' + 'sub',
									access: true,
								},
								create: true,
								view: true,
								update: true,
								drop: true,
							}));

							return [parentObj, ...children]; // parent + its children, flattened
						}

						// non-parent item
						return {
							moduleName: {
								key: item.moduleName.key,
								value: item.moduleName.value,
								access: true,
							},
							create: true,
							view: true,
							update: true,
							drop: true,
						};
					});
					this.accessToCustom = this.customPermission;
					if (role === SYSTEM_ADMIN) {
						const resellerModules = [
							{
								moduleName: {
									key: 'companyManagement',
									value: 'Company Management',
								},
								create: false,
								view: false,
								update: false,
								drop: false,
							},
							{
								moduleName: { key: 'userManagement', value: 'User Management' },
								create: false,
								view: false,
								update: false,
								drop: false,
							},
						];
						this.accessToCustom.push(...resellerModules);
					}
				}),
		});
	};

	getCurrencies = async () => {
		return await handleFetch({
			url: currencyURL(),
			method: 'GET',
			onSuccess: (data) => runInAction(() => (this.currencies = data)),
		});
	};

	getRoles = async () => {
		return await handleFetch({
			url: getRolesURL,
			method: 'GET',
			onSuccess: (data) => runInAction(() => (this.roles = data)),
		});
	};

	getCompanies = async () => {
		return await handleFetch({
			url: companyURL(),
			method: 'GET',
			onSuccess: (data) => runInAction(() => (this.companies = data)),
		});
	};

	createUser = async () => {
		let body = this.initialUser;
		if (this.initialUser.isPermitAll === false) {
			body.userPermissions = this.accessToCustom;

			body.userPermissions.map((m) => {
				if (m.view && (m.create || m.update || m.view || m.drop))
					body.userPermissions.filter((p) => p.moduleName.key === m.moduleName.key)[0].moduleName.access = true;
				else body.userPermissions.filter((p) => p.moduleName.key === m.moduleName.key)[0].moduleName.access = false;
			});
		}

		if (body.role === TRANSLATOR) delete body.companyId;

		const userAPIURL = body._id ? userURL(body._id) : signupURL;
		const isUpdate = !!body._id;

		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url: userAPIURL,
					method: isUpdate ? 'PUT' : 'POST',
					body,
					onSuccess: () =>
						runInAction(() => {
							this.errors = {};
							this.isUserModalOpen = false;
							this.initialUser = INITIAL_USER;
							this.getUsers();
							this.selectedCompanyId = null;
						}),
					onValidationError: (errors) => runInAction(() => (this.errors = errors)),
				});
				if (!success) throw new Error('Failed to save user');
				return success;
			})(),
			{
				loading: isUpdate ? 'Updating user...' : 'Creating user...',
				success: isUpdate ? 'User updated successfully!' : 'User created successfully!',
				error: 'Failed to save user',
			},
		);
	};

	get cnpCurrencySymbol() {
		return this.currencies.find((currency) => currency.key === this.selectedCurrency.cnp)?.symbol;
	}

	get onboardCurrencySymbol() {
		return this.currencies.find((currency) => currency.key === this.selectedCurrency.onboard)?.symbol;
	}

	setUserData = (id) => {
		if (!id) return (this.initialUser = INITIAL_USER);
		const userData = this.users?.find((d) => d._id === id) || INITIAL_USER;
		this.initialUser = { ...userData };
	};

	handleEditUser = (id) => {
		this.setUserData(id);
		this.isUserModalOpen = true;
		this.getModulesByUser(id);
	};

	openDeleteConfirmModal = (userId, displayName) => {
		this.selectedUserId = userId;
		this.selectedUserDisplayName = displayName;
		this.isDeleteConfirmModalOpen = true;
	};

	closeDeleteConfirmModal = () => {
		this.isDeleteConfirmModalOpen = false;
	};

	handleDeleteUser = async () => {
		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url: userDelURL(this.selectedUserId),
					method: 'DELETE',
					onSuccess: () =>
						runInAction(() => {
							this.isDeleteConfirmModalOpen = false;
							this.getUsers();
						}),
				});
				if (!success) throw new Error('Failed to delete user');
				return success;
			})(),
			{
				loading: 'Deleting user...',
				success: 'User deleted successfully!',
				error: 'Failed to delete user',
			},
		);
	};
}

export const userStore = new UserStore();
